/**
 * ProductionReport - Main container component
 *
 * Yearly Production Report for water resource management.
 * Displays production data aggregated by Aquifer, Agg System, and individual wells.
 */

import React, { useState, useMemo, useRef } from "react";
import { Typography, Button, ButtonGroup, Collapse } from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  GetApp as ExportIcon,
} from "@material-ui/icons";
import InfoBox from "../../../../components/InfoBox";
import Link from "@material-ui/core/Link";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Panel from "../../../../components/panels/Panel";

import { COLUMN_MODES } from "./constants";
import { useProductionData, useGlobalFilters } from "./hooks";
import { buildAquiferSummary, buildAggSystemTree } from "./builders";
import { exportAquiferSummary, exportAggSystemSummary } from "./exports";
import {
  Divider,
  Breadcrumbs,
  TableSection,
  TableTitle,
  TableControls,
  CollapseIcon,
  NoDataMessage,
  ButtonContainer,
} from "./components";
import {
  FilterPanel,
  AquiferSummaryTable,
  AggSystemSummaryTable,
  DetailTable,
  ExcelTable,
} from "./components/organisms";

/**
 * Main ProductionReport component
 */
const ProductionReport = () => {
  // Data fetching
  const { rawData, filterOptions, error, isFetching } = useProductionData();

  // Column visibility mode
  const [columnMode, setColumnMode] = useState(COLUMN_MODES.full);

  // Drilldown mode for Table 2
  const [drillMode, setDrillMode] = useState("year-wells");

  // Section collapse state
  const [aquiferSectionOpen, setAquiferSectionOpen] = useState(false);
  const [aggSystemSectionOpen, setAggSystemSectionOpen] = useState(false);
  const [detailSectionOpen, setDetailSectionOpen] = useState(true);
  const [excelStyleSectionOpen, setExcelStyleSectionOpen] = useState(true);

  // Refs for table export functions
  const detailTableRef = useRef(null);
  const excelTableRef = useRef(null);

  // Global filters
  const {
    selectedYears,
    selectedAquifers,
    selectedAggSystems,
    selectedWells,
    setSelectedYears,
    setSelectedAquifers,
    setSelectedAggSystems,
    setSelectedWells,
    selectedYearsSet,
    selectedAquifersSet,
    selectedAggSystemsSet,
    selectedWellsSet,
    includeNoPermit,
    isWellFilterActive,
    handleResetFilters,
  } = useGlobalFilters(filterOptions);

  // Filter raw data based on selections
  // Only depend on Set versions to avoid redundant re-renders (Sets are derived from arrays)
  const filteredRows = useMemo(() => {
    if (!rawData) return [];

    if (
      selectedYearsSet.size === 0 ||
      selectedAquifersSet.size === 0 ||
      selectedAggSystemsSet.size === 0 ||
      selectedWellsSet.size === 0
    ) {
      return [];
    }

    return rawData.filter((row) => {
      // Year, aquifer, and well must match
      if (!selectedYearsSet.has(row.report_year)) return false;
      if (!selectedAquifersSet.has(row.aquifer_ndx)) return false;
      if (!selectedWellsSet.has(row.well_ndx)) return false;

      // Agg system: either matches a selected system, or is null and "No Permit" is selected
      const aggSystemMatch =
        selectedAggSystemsSet.has(row.agg_system_ndx) ||
        (row.agg_system_ndx === null && includeNoPermit);

      return aggSystemMatch;
    });
  }, [
    rawData,
    selectedYearsSet,
    selectedAquifersSet,
    selectedAggSystemsSet,
    selectedWellsSet,
    includeNoPermit,
  ]);

  // Memoized aggregations
  const aquiferSummary = useMemo(
    () => buildAquiferSummary(filteredRows),
    [filteredRows]
  );

  const aggSystemSummary = useMemo(
    () => buildAggSystemTree(filteredRows, drillMode),
    [filteredRows, drillMode]
  );

  if (error) return "An error has occurred: " + error.message;

  return (
    <>
      <Helmet title="Yearly Production Report" />
      <Typography variant="h3" gutterBottom display="inline">
        Yearly Production Report
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Yearly Production</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {/* Description Section */}
      <InfoBox title="About This Report">
        <Typography variant="body2" color="textSecondary" paragraph>
          This report displays water production data organized by permit
          allocations. Permits are assigned at the{" "}
          <strong>Aggregate System</strong> level, meaning multiple wells may
          share a single permit allocation. Wells listed as "Not Aggregated"
          have individual permits per well.
        </Typography>
        <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
          How Totals Work
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          All totals and percentages are calculated{" "}
          <strong>on-the-fly from filtered data</strong>. When you filter by
          aggregate systems, aquifers, years, or wells, only the selected items
          contribute to the totals. Permits are counted once per system per year
          (not per well) to avoid double-counting shared allocations.
        </Typography>
        <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
          Filters
        </Typography>
        <Typography variant="body2" color="textSecondary" component="div">
          <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
            <li>
              <strong>Years:</strong> Filter by reporting year(s)
            </li>
            <li>
              <strong>Aquifers:</strong> Filter by aquifer
            </li>
            <li>
              <strong>Agg Systems:</strong> Filter by aggregate system (permit
              holder) - search by system name or well number
            </li>
            <li>
              <strong>Wells:</strong> Filter to specific wells to view
              individual well contributions against their system's permit
              allocations
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
          Tables
        </Typography>
        <Typography variant="body2" color="textSecondary" component="div">
          <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
            <li>
              <strong>Summary by Aquifer:</strong> Production totals rolled up
              by aquifer, then year, then aggregate system
            </li>
            <li>
              <strong>Summary by Aggregate System:</strong> Production totals by
              permit holder with drilldown options
            </li>
            <li>
              <strong>Well Detail:</strong> Individual well records with column
              filtering, sorting, and pagination
            </li>
            <li>
              <strong>Grouped by Agg System:</strong> Excel-style grouped view
              with subtotals per aggregate system
            </li>
          </ul>
        </Typography>
      </InfoBox>

      {/* Filters Section */}
      <FilterPanel
        filterOptions={filterOptions}
        selectedYears={selectedYears}
        setSelectedYears={setSelectedYears}
        selectedAquifers={selectedAquifers}
        setSelectedAquifers={setSelectedAquifers}
        selectedAggSystems={selectedAggSystems}
        setSelectedAggSystems={setSelectedAggSystems}
        selectedWells={selectedWells}
        setSelectedWells={setSelectedWells}
        isWellFilterActive={isWellFilterActive}
        recordCount={filteredRows.length}
        columnMode={columnMode}
        setColumnMode={setColumnMode}
        onResetFilters={handleResetFilters}
      />

      {/* Table 1: Aquifer Summary */}
      <TableSection>
        <TableControls>
          <TableTitle
            variant="h5"
            onClick={() => setAquiferSectionOpen(!aquiferSectionOpen)}
          >
            <CollapseIcon>
              {aquiferSectionOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </CollapseIcon>
            Summary by Aquifer
          </TableTitle>
          {aquiferSectionOpen && (
            <Button
              size="small"
              startIcon={<ExportIcon />}
              onClick={() => exportAquiferSummary(aquiferSummary, columnMode)}
              variant="outlined"
            >
              Export CSV
            </Button>
          )}
        </TableControls>
        <Collapse in={aquiferSectionOpen}>
          <Panel>
            {isFetching ? (
              <NoDataMessage>
                <Typography>Loading...</Typography>
              </NoDataMessage>
            ) : (
              <AquiferSummaryTable
                summary={aquiferSummary}
                columnMode={columnMode}
              />
            )}
          </Panel>
        </Collapse>
      </TableSection>

      {/* Table 2: Agg System Summary */}
      <TableSection>
        <TableControls>
          <TableTitle
            variant="h5"
            onClick={() => setAggSystemSectionOpen(!aggSystemSectionOpen)}
          >
            <CollapseIcon>
              {aggSystemSectionOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </CollapseIcon>
            Summary by Aggregate System
          </TableTitle>
          {aggSystemSectionOpen && (
            <ButtonContainer>
              <ButtonGroup size="small" variant="outlined">
                <Button
                  variant={
                    drillMode === "year-wells" ? "contained" : "outlined"
                  }
                  color={drillMode === "year-wells" ? "primary" : "default"}
                  onClick={() => setDrillMode("year-wells")}
                >
                  Year → Wells
                </Button>
                <Button
                  variant={
                    drillMode === "wells-year" ? "contained" : "outlined"
                  }
                  color={drillMode === "wells-year" ? "primary" : "default"}
                  onClick={() => setDrillMode("wells-year")}
                >
                  Wells → Year
                </Button>
              </ButtonGroup>
              <Button
                size="small"
                startIcon={<ExportIcon />}
                onClick={() =>
                  exportAggSystemSummary(
                    aggSystemSummary,
                    columnMode,
                    drillMode
                  )
                }
                variant="outlined"
              >
                Export CSV
              </Button>
            </ButtonContainer>
          )}
        </TableControls>
        <Collapse in={aggSystemSectionOpen}>
          <Panel>
            {isFetching ? (
              <NoDataMessage>
                <Typography>Loading...</Typography>
              </NoDataMessage>
            ) : (
              <AggSystemSummaryTable
                summary={aggSystemSummary}
                mode={drillMode}
                columnMode={columnMode}
              />
            )}
          </Panel>
        </Collapse>
      </TableSection>

      {/* Table 3: Detail View */}
      <TableSection>
        <TableControls>
          <TableTitle
            variant="h5"
            onClick={() => setDetailSectionOpen(!detailSectionOpen)}
          >
            <CollapseIcon>
              {detailSectionOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </CollapseIcon>
            Well Detail
          </TableTitle>
          {detailSectionOpen && (
            <Button
              size="small"
              startIcon={<ExportIcon />}
              onClick={() => detailTableRef.current?.exportCsv()}
              variant="outlined"
            >
              Export CSV
            </Button>
          )}
        </TableControls>
        <Collapse in={detailSectionOpen}>
          <Panel>
            {isFetching ? (
              <NoDataMessage>
                <Typography>Loading...</Typography>
              </NoDataMessage>
            ) : (
              <DetailTable
                ref={detailTableRef}
                rows={filteredRows}
                columnMode={columnMode}
              />
            )}
          </Panel>
        </Collapse>
      </TableSection>

      {/* Table 4: Excel-Style View */}
      <TableSection>
        <TableControls>
          <TableTitle
            variant="h5"
            onClick={() => setExcelStyleSectionOpen(!excelStyleSectionOpen)}
          >
            <CollapseIcon>
              {excelStyleSectionOpen ? (
                <ExpandMoreIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </CollapseIcon>
            Grouped by Agg System (Excel-Style)
          </TableTitle>
          {excelStyleSectionOpen && (
            <Button
              size="small"
              startIcon={<ExportIcon />}
              onClick={() => excelTableRef.current?.exportCsv()}
              variant="outlined"
            >
              Export CSV
            </Button>
          )}
        </TableControls>
        <Collapse in={excelStyleSectionOpen}>
          <Panel>
            {isFetching ? (
              <NoDataMessage>
                <Typography>Loading...</Typography>
              </NoDataMessage>
            ) : (
              <ExcelTable
                ref={excelTableRef}
                rows={filteredRows}
                columnMode={columnMode}
              />
            )}
          </Panel>
        </Collapse>
      </TableSection>
    </>
  );
};

export default ProductionReport;
